
import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { emojiData, searchEmojis, getRecentEmojis, EmojiCategory, EmojiItem } from './EmojiData';
import { Search, Clock } from 'lucide-react';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  children: React.ReactNode;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('recent');
  
  const recentEmojis = useMemo(() => getRecentEmojis(), []);
  const searchResults = useMemo(() => searchEmojis(searchQuery), [searchQuery]);

  const handleEmojiSelect = (emoji: string) => {
    onEmojiSelect(emoji);
    setIsOpen(false);
    setSearchQuery('');
    setActiveCategory('recent');
  };

  const handleCategoryClick = (categoryName: string) => {
    setActiveCategory(categoryName);
    setSearchQuery('');
  };

  const renderEmojiGrid = (emojis: EmojiItem[]) => (
    <div className="grid grid-cols-8 gap-1 p-2">
      {emojis.map((emoji, index) => (
        <button
          key={`${emoji.emoji}-${index}`}
          onClick={() => handleEmojiSelect(emoji.emoji)}
          className="w-8 h-8 flex items-center justify-center hover:bg-accent rounded text-lg transition-colors"
          title={emoji.name}
        >
          {emoji.emoji}
        </button>
      ))}
    </div>
  );

  const getCurrentEmojis = (): EmojiItem[] => {
    if (searchQuery.trim()) {
      return searchResults;
    }
    
    if (activeCategory === 'recent') {
      return recentEmojis;
    }
    
    const category = emojiData.find(cat => cat.name === activeCategory);
    return category ? category.emojis : [];
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 border shadow-lg" 
        align="end" 
        side="bottom"
        sideOffset={4}
        avoidCollisions={true}
        collisionPadding={8}
      >
        <div className="flex flex-col h-96">
          {/* Search Bar */}
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search emojis..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8"
              />
            </div>
          </div>

          {/* Category Tabs */}
          {!searchQuery && (
            <div className="flex overflow-x-auto border-b bg-muted/30">
              <button
                onClick={() => handleCategoryClick('recent')}
                className={`flex-shrink-0 p-2 text-sm border-b-2 transition-colors ${
                  activeCategory === 'recent'
                    ? 'border-primary text-primary'
                    : 'border-transparent hover:text-foreground'
                }`}
                title="Recently Used"
              >
                <Clock className="h-4 w-4" />
              </button>
              {emojiData.map((category) => (
                <button
                  key={category.name}
                  onClick={() => handleCategoryClick(category.name)}
                  className={`flex-shrink-0 p-2 text-lg border-b-2 transition-colors ${
                    activeCategory === category.name
                      ? 'border-primary'
                      : 'border-transparent hover:bg-accent'
                  }`}
                  title={category.name}
                >
                  {category.icon}
                </button>
              ))}
            </div>
          )}

          {/* Emoji Grid */}
          <ScrollArea className="flex-1">
            <div className="min-h-0">
              {searchQuery && searchResults.length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No emojis found for "{searchQuery}"
                </div>
              ) : getCurrentEmojis().length === 0 ? (
                <div className="p-4 text-center text-muted-foreground">
                  No emojis in this category
                </div>
              ) : (
                renderEmojiGrid(getCurrentEmojis())
              )}
            </div>
          </ScrollArea>

          {/* Category Label */}
          {!searchQuery && (
            <div className="p-2 border-t bg-muted/30 text-xs text-muted-foreground text-center">
              {activeCategory === 'recent' ? 'Recently Used' : activeCategory}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default EmojiPicker;
